import React, { useRef, useEffect, KeyboardEvent } from 'react';
import { Form, Dropdown, Button } from 'react-bootstrap';
import { Search, Cloud, Upload, Filter, FolderPlus } from 'lucide-react';

interface DataSourceFilterProps {
    source: 'all' | 'file_upload' | 'drive_sync';
    searchTerm: string;
    onSourceChange: (source: 'all' | 'file_upload' | 'drive_sync') => void;
    onSearchChange: (searchTerm: string) => void;
    onSearch: (searchTerm: string) => void;  // New prop for handling search submit
    onUpload?: () => void;
    onDriveSync?: () => void;
    onCreateFolder?: () => void;
    disabled?: boolean;
}

const DataSourceFilter: React.FC<DataSourceFilterProps> = ({
                                                               source,
                                                               searchTerm,
                                                               onSourceChange,
                                                               onSearchChange,
                                                               onSearch,
                                                               onUpload,
                                                               onDriveSync,
                                                               onCreateFolder,
                                                               disabled = false
                                                           }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const input = inputRef.current;
        if (input && document.activeElement !== input) {
            input.focus();
        }
    }, []);

    const handleDriveSyncClick = () => {
        console.log('Drive sync button clicked in DataSourceFilter');
        if (onDriveSync) {
            onDriveSync();
        }
    };

    const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            onSearch(searchTerm);
        }
    };

    const handleSearchClick = () => {
        onSearch(searchTerm);
    };

    const handleClearSearch = () => {
        onSearchChange('');
        onSearch('');
    };

    const getSourceLabel = (sourceType: 'all' | 'file_upload' | 'drive_sync') => {
        switch (sourceType) {
            case 'file_upload':
                return 'Uploaded Files';
            case 'drive_sync':
                return 'Drive Files';
            default:
                return 'All Files';
        }
    };

    return (
        <div className="d-flex gap-4 flex-grow-1">
            <div style={{ minWidth: '200px' }}>
                <Dropdown>
                    <Dropdown.Toggle
                        variant="light"
                        className="w-100 d-flex align-items-center justify-content-between border"
                        disabled={disabled}
                        style={{ height: '38px' }}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <Filter size={16} className="text-muted" />
                            <span className="text-truncate">{getSourceLabel(source)}</span>
                        </div>
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="w-100 shadow-sm border-0">
                        <Dropdown.Item
                            active={source === 'all'}
                            onClick={() => onSourceChange('all')}
                            className="d-flex align-items-center gap-2 py-2"
                        >
                            <Search size={16} />
                            <span>All Files</span>
                        </Dropdown.Item>
                        <Dropdown.Item
                            active={source === 'file_upload'}
                            onClick={() => onSourceChange('file_upload')}
                            className="d-flex align-items-center gap-2 py-2"
                        >
                            <Upload size={16} />
                            <span>Uploaded Files</span>
                        </Dropdown.Item>
                        <Dropdown.Item
                            active={source === 'drive_sync'}
                            onClick={() => onSourceChange('drive_sync')}
                            className="d-flex align-items-center gap-2 py-2"
                        >
                            <Cloud size={16} />
                            <span>Drive Files</span>
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <div className="flex-grow-1">
                <div className="d-flex gap-2 align-items-center">
                    <div className="position-relative flex-grow-1">
                        <div className="position-absolute top-50 translate-middle-y ps-3">
                            <Search
                                size={16}
                                className="text-muted cursor-pointer"
                                onClick={handleSearchClick}
                            />
                        </div>
                        <Form.Control
                            ref={inputRef}
                            type="text"
                            placeholder="Search files and folders..."
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            onKeyPress={handleKeyPress}
                            autoComplete="off"
                            disabled={disabled}
                            className="ps-5 border"
                            style={{ height: '38px' }}
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                className="btn btn-link position-absolute top-50 end-0 translate-middle-y pe-3 py-0"
                                onClick={handleClearSearch}
                                disabled={disabled}
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        )}
                    </div>

                    <Button
                        variant="outline-primary"
                        className="d-flex align-items-center justify-content-center p-2"
                        style={{ height: '38px', width: '38px' }}
                        onClick={onUpload}
                        disabled={disabled}
                        title="Upload from Laptop"
                    >
                        <Upload size={16} />
                    </Button>

                    <Button
                        variant="outline-primary"
                        className="d-flex align-items-center justify-content-center p-2"
                        style={{ height: '38px', width: '38px' }}
                        onClick={handleDriveSyncClick}
                        disabled={disabled}
                        title="Sync from Google Drive"
                    >
                        <Cloud size={16} />
                    </Button>

                    <Button
                        variant="outline-primary"
                        className="d-flex align-items-center justify-content-center p-2"
                        style={{ height: '38px', width: '38px' }}
                        onClick={onCreateFolder}
                        disabled={disabled}
                        title="Create New Folder"
                    >
                        <FolderPlus size={16} />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default DataSourceFilter;